'use client';

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import type { KeyboardEvent, ReactNode } from 'react';
import { Skeleton } from '@/components/ui/app-skeleton';
import { useFeaturedMarketCarousel } from '@/hooks/featured-market-hero/use-featured-market-carousel';
import { BreakingViewsWidget } from '../breaking-views/breaking-views-widget';
import { hotTakesSlideMotion } from '../breaking-views/hot-takes-slide-motion';
import { RightColumnSkeleton } from '../breaking-views/right-column-skeleton';
import { TopMarketsByPrizeWidget } from '../breaking-views/top-markets-by-prize-widget';
import { FeaturedMarketHeroSkeleton } from './featured-market-hero-skeleton';
import { FeaturedMarketHero } from './index';
import { useHeroCandidates } from './use-hero-markets';
interface MarketsFeaturedSectionProps {
  readonly visible: boolean;
  readonly watchlistPubkeys: readonly string[];
}

/** Own the live candidates and carousel clock without updating the feed owner. */
export function MarketsFeaturedSection(t0) {
  const $ = _c(22);
  const {
    visible,
    watchlistPubkeys
  } = t0;
  const t = useTranslations("markets");
  const {
    candidates: heroRows,
    isPending: heroPending
  } = useHeroCandidates();
  const reduceMotion = useReducedMotion();
  const t1 = reduceMotion === true;
  let t2;
  if ($[0] !== heroRows || $[1] !== t1) {
    t2 = {
      candidateMarkets: heroRows,
      prefersReducedMotion: t1
    };
    $[0] = heroRows;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const carousel = useFeaturedMarketCarousel(t2);
  const featuredHeroMarket = carousel.currentMarket;
  let t3;
  if ($[3] !== carousel.direction || $[4] !== carousel.slidePace || $[5] !== featuredHeroMarket || $[6] !== reduceMotion) {
    t3 = featuredHeroMarket !== null ? hotTakesSlideMotion(carousel.direction, reduceMotion === true, carousel.slidePace) : null;
    $[3] = carousel.direction;
    $[4] = carousel.slidePace;
    $[5] = featuredHeroMarket;
    $[6] = reduceMotion;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const featuredHeroSlideMotion = t3;
  const showFeaturedHero = visible && (heroPending || featuredHeroMarket !== null);
  let t4;
  if ($[8] !== carousel) {
    t4 = function handleCarouselKeyDown(event) {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        carousel.prev("keyboard");
        return;
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        carousel.next("keyboard");
      }
    };
    $[8] = carousel;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleCarouselKeyDown = t4;
  let t5;
  if ($[10] !== carousel || $[11] !== featuredHeroMarket || $[12] !== featuredHeroSlideMotion || $[13] !== handleCarouselKeyDown || $[14] !== heroPending || $[15] !== reduceMotion || $[16] !== showFeaturedHero || $[17] !== t || $[18] !== watchlistPubkeys) {
    t5 = showFeaturedHero && <m.div key="featured-hero" className="hidden origin-top overflow-hidden md:block" initial={reduceMotion === true ? false : {
      opacity: 0,
      scaleY: 0.98
    }} animate={{
      opacity: 1,
      scaleY: 1
    }} exit={reduceMotion === true ? {
      opacity: 0
    } : {
      opacity: 0,
      scaleY: 0.98
    }} transition={reduceMotion === true ? {
      duration: 0
    } : {
      duration: 0.18,
      ease: "easeOut"
    }}>{carousel.currentMarket === null && heroPending ? <div className="hidden w-full md:mb-2 md:flex md:gap-4 md:items-stretch" data-testid="featured-market-hero-skeleton-wrapper"><div className="md:basis-[70%] md:max-w-[70%] md:shrink-0"><FeaturedMarketHeroSkeleton /></div><div className="relative md:flex-1 md:min-w-0"><div className="md:absolute md:inset-x-0 md:top-0 md:bottom-12 flex min-h-0 flex-col"><RightColumnSkeleton className="flex-1" /></div><div className="md:absolute md:bottom-0 md:left-0 md:right-0"><Skeleton className="h-9 w-full rounded-full" /></div></div></div> : <div className="hidden w-full md:mb-2 md:flex md:gap-4 md:items-stretch"><section className="md:basis-[70%] md:max-w-[70%] md:shrink-0" onPointerEnter={carousel.pause} onPointerLeave={carousel.resume} onFocus={carousel.pause} onBlur={carousel.resume} onKeyDown={handleCarouselKeyDown} aria-roledescription="carousel" aria-label={t("heading")}><div className="relative overflow-hidden"><HeroSlidePresence>{featuredHeroMarket !== null && featuredHeroSlideMotion !== null ? <m.div key={featuredHeroMarket.pubkey} initial={featuredHeroSlideMotion.initial} animate={featuredHeroSlideMotion.animate} exit={featuredHeroSlideMotion.exit} transition={featuredHeroSlideMotion.transition}><FeaturedMarketHero market={featuredHeroMarket} slot={carousel.currentIndex} watchlistPubkeys={watchlistPubkeys} carousel={{
                  slides: carousel.slides,
                  currentIndex: carousel.currentIndex,
                  onPrev: () => carousel.prev("pill"),
                  onNext: () => carousel.next("pill")
                }} /></m.div> : null}</HeroSlidePresence></div></section><div className="relative md:flex-1 md:min-w-0"><div className="md:absolute md:inset-0 flex min-h-0 flex-col gap-4"><BreakingViewsWidget className="shrink-0" /><TopMarketsByPrizeWidget /></div></div></div>}</m.div>;
    $[10] = carousel;
    $[11] = featuredHeroMarket;
    $[12] = featuredHeroSlideMotion;
    $[13] = handleCarouselKeyDown;
    $[14] = heroPending;
    $[15] = reduceMotion;
    $[16] = showFeaturedHero;
    $[17] = t;
    $[18] = watchlistPubkeys;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t5) {
    t6 = <LazyMotion features={domAnimation}><AnimatePresence initial={false}>{t5}</AnimatePresence></LazyMotion>;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
}
function HeroSlidePresence(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <AnimatePresence mode="wait" initial={false}>{children}</AnimatePresence>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}